import type { FileRejection } from './types';

/**
 * Whether a file matches an `accept` string, with the browser's own rules: a `.ext` token matches
 * the name, `type/*` matches the MIME group and anything else must equal the MIME type. The picker
 * applies `accept` itself, but a drop does not, which is how a dropped `.gif` used to reach an
 * image field unchecked.
 */
export const acceptsFile = (file: File, accept: string | undefined) => {
  if (!accept?.trim()) return true;

  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();

  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith('.')) return name.endsWith(token);
      if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
      return type === token;
    });
};

type Options = { accept?: string; maxSize?: number; multiple?: boolean };

/** Splits a picked or dropped list into what the field takes and what it turns away, with reasons. */
export const selectFiles = (files: File[], { accept, maxSize, multiple }: Options) => {
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];

  for (const file of files) {
    if (!acceptsFile(file, accept)) rejected.push({ file, reason: 'type' });
    else if (maxSize !== undefined && file.size > maxSize) rejected.push({ file, reason: 'size' });
    else if (!multiple && accepted.length > 0) rejected.push({ file, reason: 'count' });
    else accepted.push(file);
  }

  return { accepted, rejected };
};
